import escapeStringRegexp from 'escape-string-regexp';
import { ComponentFilterProps, withFilter } from 'hoc/withFilter';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { HeaderConfigContext } from 'app/App';
import { ComponentSortProps, withSort } from 'hoc/withSort';
import { useUrlParams } from 'hooks/use-url-params';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { dumpsListSorters } from './sorting-schema';
import Row from './components/Row';
import { FilterPanel } from './components/FilterPanel';
import { StyledContent, StyledList, StyledSort, StyledSummary, StyledTriggerContainer } from './styles';
import { GroupsFilterFormValues } from '../Groups/types';
import { HeaderExtra } from './components/HeaderExtra';
import { useDumpsPageState } from './hooks/use-dumps-page-state';
import { useGetDumps } from './hooks/use-get-dumps';
import { useDumpsState } from './hooks/use-dumps-state';
import { SortSelect, SortSelectProps } from './components/SortSelect';
import { DumpListSort, ExportFolder } from './types';
import { TriggerByMonth } from './components/TriggerByMonth';

type AdministartionProps = ComponentFilterProps<GroupsFilterFormValues> & ComponentSortProps<SortSelectProps>;
const DumpsPage: React.FC<AdministartionProps> = ({ filterManager, sortManager }) => {
  useGetDumps();
  const { setHeaderRenderer } = useContext(HeaderConfigContext);
  const { queryUrlParams, onPushUrlParams } = useUrlParams({});

  const { dumps, isDumpsLoaded, isDumpsLoading } = useDumpsState();
  const { dumpsPageState, handleUpdatedumpsPageState } = useDumpsPageState(dumps, queryUrlParams);

  const [filteredGroups, setFilteredGroups] = useState(dumpsPageState.dumps);

  const { sorters, SortComponent } = sortManager;
  const currentSort: DumpListSort = (queryUrlParams.sort as DumpListSort) || DumpListSort.DATE_DESC;

  const onSort = (sort: DumpListSort) => {
    onPushUrlParams({ sort });
  };

  const { t } = useTranslation();
  const {
    goBack,
    location: { search },
  } = useHistory();
  const { handleFilterOpen, handleFilterClose, isFilterActive, isFilterSubmitted, state, handleFilterSubmitted } =
    filterManager;

  const onSelectedGroup = (group: ExportFolder) => {
    handleUpdatedumpsPageState({ selectedGroup: group });
    onPushUrlParams({ group: group.id });
  };

  const onFilter = () => {
    const list = dumpsPageState?.dumps ?? [];
    if (list.length < 1) {
      setFilteredGroups(dumpsPageState.dumps ?? []);
      return;
    }
    if (dumpsPageState.dumps && state.search) {
      const regExp = new RegExp(escapeStringRegexp(state.search || ''), 'i');
      const selectedGroup = dumpsPageState.dumps.filter(({ name }) => name && regExp.test(name))[0] || null;

      setFilteredGroups(dumpsPageState.dumps.filter(({ name }) => name && regExp.test(name)));
      handleUpdatedumpsPageState({ selectedGroup });
      handleFilterSubmitted(false);
    }
  };

  useEffect(() => {
    onFilter();
  }, [queryUrlParams]);

  useEffect(() => {
    setFilteredGroups(dumpsPageState.dumps ?? dumps ?? []);
  }, [dumpsPageState.dumps, dumps]);

  useEffect(() => {
    setHeaderRenderer(
      <HeaderExtra isFilterActive={isFilterActive} onFilterOpen={handleFilterOpen} onFilterClose={handleFilterClose} />
    );

    return () => setHeaderRenderer(null);
  }, [setHeaderRenderer, isFilterActive]);

  useEffect(() => {
    const all = dumpsPageState?.dumps ?? dumps ?? [];
    const regExp = state.search ? new RegExp(escapeStringRegexp(state.search), 'i') : null;
    const filtered = regExp ? all.filter(({ name }) => name && regExp.test(name)) : all;

    const sorter = dumpsListSorters[currentSort] || dumpsListSorters[DumpListSort.DATE_DESC];
    const sorted = [...filtered].sort(sorter);

    setFilteredGroups(sorted);
  }, [dumpsPageState.dumps, dumps, state.search, currentSort]);

  return (
    <StyledContent>
      <StyledSummary>
        <StyledTriggerContainer>
          <TriggerByMonth folders={dumpsPageState?.dumps} loading={isDumpsLoading} />
        </StyledTriggerContainer>
        <StyledSort>
          <SortComponent defaultValue={queryUrlParams.sort || DumpListSort.DATE_DESC} onChange={onSort} />
        </StyledSort>
        <StyledList>
          {filteredGroups?.map((dump) => {
            return (
              <div key={dump.id}>
                <Row
                  item={dump}
                  onItemClick={onSelectedGroup}
                  selectedItem={dumpsPageState.selectedGroup}
                  isListLoaded={isDumpsLoaded}
                />
              </div>
            );
          })}
        </StyledList>
      </StyledSummary>
    </StyledContent>
  );
};

function formatUrlParams({ search }: Record<string, any>): Partial<any> {
  return {
    search: search || undefined,
  };
}

export default withFilter({
  filterProps: { initialState: { search: '' }, formatUrlParams },
  FilterContent: FilterPanel,
})(withSort({ sorters: dumpsListSorters, SortComponent: SortSelect })(DumpsPage));
